import { Link } from 'react-router-dom'
import { FiCheckCircle, FiXCircle, FiFileText } from 'react-icons/fi'
import BlogLayout from '../../components/BlogLayout'

const toc = [
  { id: 'que-revisa', label: 'Qué revisa la ITSS en una visita por jornada' },
  { id: 'como-llega', label: 'Cómo llega la inspección a tu puerta' },
  { id: 'documentos', label: 'Documentos que debes preparar' },
  { id: 'durante-visita', label: 'Cómo comportarte durante la visita' },
  { id: 'requerimiento', label: 'Si recibes un requerimiento por escrito' },
  { id: 'errores-fatales', label: 'Errores fatales que disparan la sanción' },
  { id: 'acta-infraccion', label: 'Si te levantan acta: pasos siguientes' },
  { id: 'plantilla', label: 'Plantilla de respuesta a requerimiento' },
  { id: 'faq', label: 'Preguntas frecuentes' },
]

const faqs = [
  { question: '¿Cuánto tiempo tengo para responder a un requerimiento?', answer: 'El plazo habitual son 10 días hábiles desde la notificación, ampliables a petición motivada. Si no respondes, se levanta acta directamente con los hechos comprobados por el inspector.' },
  { question: '¿Puede entrar el inspector sin avisar?', answer: 'Sí. La ITSS tiene la facultad de visita sin previo aviso a centros de trabajo. Sus credenciales son suficientes y no necesita orden judicial salvo para domicilios particulares.' },
  { question: '¿Tengo derecho a estar acompañado por un abogado?', answer: 'Sí, pero no puede paralizar la actuación. Si el inspector quiere ver el registro horario, hay que enseñarlo con o sin abogado presente. La asistencia letrada se valora más en la fase posterior (alegaciones).' },
  { question: '¿Qué pasa si los registros están en la nube de un proveedor?', answer: 'Debes facilitar acceso al panel o exportar al instante. La excusa "lo tiene mi proveedor" no es válida: la responsabilidad de la conservación es de la empresa.' },
  { question: '¿Puedo recurrir el acta?', answer: 'Sí. Tienes 15 días para presentar alegaciones ante la autoridad sancionadora. Si la sanción finalmente se impone, hay un segundo recurso administrativo y, en última instancia, contencioso-administrativo.' },
]

export default function InspeccionTrabajoControlHorario() {
  return (
    <BlogLayout slug="inspeccion-trabajo-control-horario" toc={toc} faqs={faqs}>
      <section id="que-revisa" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Qué revisa la ITSS en una visita por jornada</h2>
        <p>
          La Inspección de Trabajo y Seguridad Social tiene unas líneas prioritarias anuales. Desde 2023 el control de jornada está entre las primeras tres en términos de número de actuaciones, y la presión inspectora aumentará con el <Link to="/blog/real-decreto-2026-fichaje-digital">nuevo Real Decreto de fichaje digital previsto para 2026</Link>. En una visita típica, el inspector verifica:
        </p>
        <ul className="space-y-2 my-4">
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" /> Existencia de un sistema de registro horario operativo y conocido por todos los trabajadores.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" /> Registros completos del último mes y muestreo de meses anteriores (hasta 4 años).</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" /> Coherencia entre registros, contratos, calendario laboral y nómina.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" /> Cómputo correcto de horas extras y su retribución/compensación.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" /> Información a la representación legal de los trabajadores (si existe).</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" /> Acuerdo o consulta documentada con el comité para definir el método.</li>
        </ul>
      </section>

      <section id="como-llega" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Cómo llega la inspección a tu puerta</h2>
        <p>Una actuación inspectora puede iniciarse por tres vías:</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2">1. Denuncia</h3>
            <p className="text-sm text-gray-600">Anónima o identificada. Suele venir de un trabajador o ex-trabajador. Provoca actuación obligatoria.</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2">2. Plan anual</h3>
            <p className="text-sm text-gray-600">Sectores y zonas seleccionadas por la ITSS según riesgo. La probabilidad varía mucho por CCAA.</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2">3. Cruce de datos</h3>
            <p className="text-sm text-gray-600">SEPE, TGSS, Hacienda. Inconsistencias entre nómina, contrato y cotización disparan visitas.</p>
          </div>
        </div>
        <p>
          Una empresa con un trabajador descontento que llama a la ITSS tiene altas probabilidades de recibir visita en menos de 60 días. La preparación tiene que ser permanente, no reactiva.
        </p>
      </section>

      <section id="documentos" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Documentos que debes preparar</h2>
        <p>Ten esta carpeta lista en todo momento:</p>
        <div className="bg-gray-50 rounded-2xl p-6 my-6 border border-gray-100">
          <ol className="space-y-2 text-sm">
            <li className="flex items-start gap-2"><FiFileText className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" /> Contratos vigentes de cada trabajador con jornada y distribución horaria.</li>
            <li className="flex items-start gap-2"><FiFileText className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" /> Calendario laboral del año en curso, comunicado y firmado.</li>
            <li className="flex items-start gap-2"><FiFileText className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" /> Acuerdo o consulta con la representación legal sobre método de registro.</li>
            <li className="flex items-start gap-2"><FiFileText className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" /> Registros horarios diarios mes a mes durante 4 años.</li>
            <li className="flex items-start gap-2"><FiFileText className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" /> Nóminas con horas extras correctamente identificadas.</li>
            <li className="flex items-start gap-2"><FiFileText className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" /> Procedimiento interno escrito de fichaje (qué hacer si me olvido, si hay incidencia, etc.).</li>
            <li className="flex items-start gap-2"><FiFileText className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" /> Información entregada a los trabajadores sobre el sistema (RGPD si hay datos personales).</li>
            <li className="flex items-start gap-2"><FiFileText className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" /> Convenio colectivo aplicable y cualquier acuerdo de empresa relevante.</li>
          </ol>
        </div>
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg my-6">
          <p className="text-blue-900 text-sm"><strong>Pro-tip:</strong> exporta de tu sistema digital un PDF firmado mensual con todos los registros. Sirve como prueba inmediata el día de la visita.</p>
        </div>
      </section>

      <section id="durante-visita" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Cómo comportarte durante la visita</h2>
        <ol className="space-y-3 my-6">
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
            <div><strong>Identifica al inspector</strong> y solicita su carnet. Anota nombre, número y unidad.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
            <div><strong>Designa un interlocutor único</strong> (gerente o RR. HH.) que acompañe al inspector. Evita que cada empleado responda por su cuenta.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
            <div><strong>Colabora.</strong> Obstaculizar la inspección es una infracción independiente y muy grave. Los inspectores tienen amplias facultades.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
            <div><strong>No mientas ni inventes.</strong> Si te falta un dato, pide aplazamiento. Una mentira detectada agrava cualquier sanción.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">5</span>
            <div><strong>Toma notas en paralelo.</strong> Anota qué documentos pide, qué empleados entrevista y qué te dice. Útil para alegaciones posteriores.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">6</span>
            <div><strong>Pide copia de la diligencia</strong> que se levante al final de la visita. Es tu derecho.</div>
          </li>
        </ol>
      </section>

      <section id="requerimiento" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Si recibes un requerimiento por escrito</h2>
        <p>
          Las inspecciones por jornada también se gestionan a distancia. Recibes un escrito con plazo (normalmente 10 días hábiles) para aportar documentación. Pasos:
        </p>
        <ol className="space-y-2 my-4 text-gray-600">
          <li><strong className="text-gray-900">1.</strong> Calcula el plazo exacto (anota en agenda con margen de 2 días).</li>
          <li><strong className="text-gray-900">2.</strong> Recoge toda la documentación pedida en formato PDF firmado.</li>
          <li><strong className="text-gray-900">3.</strong> Redacta un escrito acompañando cada documento numerado.</li>
          <li><strong className="text-gray-900">4.</strong> Presenta la respuesta a través de la sede electrónica de la ITSS.</li>
          <li><strong className="text-gray-900">5.</strong> Conserva el acuse de recibo.</li>
        </ol>
      </section>

      <section id="errores-fatales" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Errores fatales que disparan la sanción</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
          <div className="border border-red-200 bg-red-50/50 rounded-xl p-5">
            <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2"><FiXCircle className="h-5 w-5" /> Improvisar registros</h3>
            <p className="text-sm text-red-800">Recrear hojas para "tapar" un mes vacío es delito de falsedad documental. La inspección detecta caligrafía y bolígrafo idénticos. Si vienes de <Link to="/blog/plantilla-excel-control-horario">una plantilla Excel sin trazabilidad</Link>, las modificaciones retroactivas se notan en metadatos.</p>
          </div>
          <div className="border border-red-200 bg-red-50/50 rounded-xl p-5">
            <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2"><FiXCircle className="h-5 w-5" /> Ocultar registros existentes</h3>
            <p className="text-sm text-red-800">Si los tienes, enséñalos aunque sean imperfectos. Negar su existencia y que aparezcan después es muy grave.</p>
          </div>
          <div className="border border-red-200 bg-red-50/50 rounded-xl p-5">
            <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2"><FiXCircle className="h-5 w-5" /> Permitir que cada trabajador responda solo</h3>
            <p className="text-sm text-red-800">Sin coordinación, salen versiones contradictorias. Designa interlocutor y limita las entrevistas.</p>
          </div>
          <div className="border border-red-200 bg-red-50/50 rounded-xl p-5">
            <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2"><FiXCircle className="h-5 w-5" /> No tener nada por escrito</h3>
            <p className="text-sm text-red-800">"Aquí siempre hemos hecho así" no es prueba. Sin contratos detallados, calendarios y procedimientos, la inspección presume lo peor.</p>
          </div>
        </div>
      </section>

      <section id="acta-infraccion" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Si te levantan acta: pasos siguientes</h2>
        <p>El acta no es la sanción definitiva. Tienes margen de defensa:</p>
        <ol className="space-y-2 my-4 text-gray-600">
          <li><strong className="text-gray-900">15 días</strong> para presentar alegaciones contra el acta.</li>
          <li><strong className="text-gray-900">Reducción 40 %</strong> por pronto pago si reconoces los hechos.</li>
          <li><strong className="text-gray-900">Reducción 50 %</strong> si firmas acta con avenencia.</li>
          <li><strong className="text-gray-900">Recurso de alzada</strong> contra la resolución sancionadora ante el órgano superior.</li>
          <li><strong className="text-gray-900">Vía contencioso-administrativa</strong> si la resolución se confirma y consideras que es desproporcionada.</li>
        </ol>
        <p>
          La asesoría laboral o el abogado especializado son rentables en cuanto la cuantía supera 5.000 €. Para sanciones menores, suele compensar pagar con reducción y mejorar el sistema.
        </p>
      </section>

      <section id="plantilla" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Plantilla de respuesta a requerimiento</h2>
        <div className="bg-gray-50 rounded-2xl p-6 my-6 border border-gray-100 text-sm text-gray-700 font-mono whitespace-pre-line">
{`A la Inspección Provincial de Trabajo y Seguridad Social de [Provincia]

Asunto: respuesta al requerimiento nº [Nº] de [Fecha]

Don/Doña [Nombre], con NIF [NIF], en calidad de [Cargo] de la empresa [Razón social], CIF [CIF], comparece y manifiesta:

PRIMERO. Que en plazo se aporta la documentación requerida.
SEGUNDO. Que la empresa cuenta con un sistema digital de registro horario desde [Fecha], operativo, fiable y conforme al RD-ley 8/2019.
TERCERO. Que se acompañan como anexos:
  - Anexo 1: Registros horarios diarios de los meses [Periodo]
  - Anexo 2: Calendario laboral 2026 firmado
  - Anexo 3: Consulta documentada con la representación legal
  - Anexo 4: Procedimiento interno de registro
  - Anexo 5: Información entregada a los trabajadores
CUARTO. Que la empresa queda a disposición de la Inspección para cualquier aclaración.

En [Ciudad], a [Fecha].
Firma: [Cargo y nombre]
`}
        </div>
        <p className="text-xs text-gray-500">
          Adapta los anexos a tu caso. Si te faltan documentos, mejor explicarlo con sinceridad y plan de acción que omitirlos.
        </p>
      </section>

      <section id="faq" className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Preguntas frecuentes</h2>
        <div className="space-y-6">
          {faqs.map((f) => (
            <div key={f.question}>
              <h3 className="font-semibold text-gray-900 mb-1">{f.question}</h3>
              <p className="text-gray-600 text-sm">{f.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="border-t border-gray-100 pt-8 mt-12 text-sm text-gray-500">
        Si quieres que tu próxima inspección sea trámite, lee también nuestra
        <Link to="/blog/sanciones-no-fichar-2026" className="text-blue-600 hover:underline mx-1">guía de sanciones 2026</Link>
        y prepara los registros con
        <Link to="/control-horario-empresas" className="text-blue-600 hover:underline mx-1">PresenzIA</Link>.
      </div>
    </BlogLayout>
  )
}
